// src/store/useFormStore.ts
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import {
	PersonalInformation,
	ActivityOrBusinessInformation,
	CreditDetails,
	MonthlyIncomeInformation,
	MonthlyBusinessExpenses,
	MonthlyHouseExpenses,
	ValueOfAssets,
	OtherCredits
} from '@/graphql/__generated__/credit-requests';

// Define a type for all possible section identifiers
export type SectionId =
	| 'personalInformation'
	| 'activityOrBusiness'
	| 'creditDetails'
	| 'monthlyIncome'
	| 'monthlyBusinessExpenses'
	| 'monthlyHouseExpenses'
	| 'valueOfAssets'
	| 'otherCredits';

// Define the shape of the entire form state
export interface FormState {
	// The actual form data
	personalInfo: PersonalInformation;
	activityOrBusiness: ActivityOrBusinessInformation;
	creditDetails: CreditDetails;
	monthlyIncome: MonthlyIncomeInformation;
	monthlyBusinessExpenses: MonthlyBusinessExpenses;
	monthlyHouseExpenses: MonthlyHouseExpenses;
	valueOfAssets: ValueOfAssets;
	otherCredits: OtherCredits;

	// Metadata about form completion status
	completedSections: Set<SectionId>;

	// Actions to manipulate the state
	updatePersonalInfo: (data: Partial<PersonalInformation>) => void;
	updateActivityOrBusiness: (data: Partial<ActivityOrBusinessInformation>) => void;
	updateCreditDetails: (data: Partial<CreditDetails>) => void;
	updateMonthlyIncome: (data: Partial<MonthlyIncomeInformation>) => void;
	updateMonthlyBusinessExpenses: (data: Partial<MonthlyBusinessExpenses>) => void;
	updateMonthlyHouseExpenses: (data: Partial<MonthlyHouseExpenses>) => void;
	updateValueOfAssets: (data: Partial<ValueOfAssets>) => void;
	updateOtherCredits: (data: Partial<OtherCredits>) => void;

	// General section update method (can be used as an alternative)
	updateSection: <T extends keyof FormState>(sectionId: T, data: Partial<FormState[T]>) => void;

	// Get all form data (for submission)
	getAllFormData: () => {
		personalInfo: PersonalInformation;
		activityOrBusiness: ActivityOrBusinessInformation;
		creditDetails: CreditDetails;
		monthlyIncome: MonthlyIncomeInformation;
		monthlyBusinessExpenses: MonthlyBusinessExpenses;
		monthlyHouseExpenses: MonthlyHouseExpenses;
		valueOfAssets: ValueOfAssets;
		otherCredits: OtherCredits;
	};

	// Reset the store to initial state
	resetForm: () => void;
}

// Initial values for each section
const initialPersonalInfo: PersonalInformation = {
	childDependants: null,
	houseType: null,
	livesWithWhichFamilyMember: null,
	transportationMethod: null,
	otherFamilyDependants: null,
	otherFamilyDependantsExplanation: null
};

const initialActivityOrBusiness: ActivityOrBusinessInformation = {
	activityDetail: null,
	activityType: null,
	startDate: null
};

const initialCreditDetails: CreditDetails = {
	amountRequested: null,
	creditUse: null,
	creditUseDetail: null,
	expectedAdditionalIncome: null
};

const initialMonthlyIncome: MonthlyIncomeInformation = {
	businessSales: null,
	familyContributionsIncome: null,
	otherSourcesIncome: null,
	otherSourcesIncomeDetail: null,
	salary: null
};

const initialMonthlyBusinessExpenses: MonthlyBusinessExpenses = {
	electricBill: null,
	internetBill: null,
	equipmentMaintenance: null,
	rent: null,
	salaries: null,
	inputsPurchase: null,
	gas: null
};

const initialMonthlyHouseExpenses: MonthlyHouseExpenses = {
	electricBill: null,
	internetBill: null,
	waterBill: null,
	rent: null,
	familyExpenses: null,
	groceries: null,
	tuitions: null
};

const initialValueOfAssets: ValueOfAssets = {
	carsValue: null,
	homeValue: null,
	equipmentValue: null,
	equipmentDetail: null,
	landValue: null,
	savings: null
};

const initialOtherCredits: OtherCredits = {
	currentTotalDebt: null,
	monthlyPaymentToCredits: null
};

export const useUpdateQuestionnaireStore = create<FormState>()(
	devtools(
		persist(
			(set, get) => ({
				// Initial state
				personalInfo: initialPersonalInfo,
				activityOrBusiness: initialActivityOrBusiness,
				creditDetails: initialCreditDetails,
				monthlyIncome: initialMonthlyIncome,
				monthlyBusinessExpenses: initialMonthlyBusinessExpenses,
				monthlyHouseExpenses: initialMonthlyHouseExpenses,
				valueOfAssets: initialValueOfAssets,
				otherCredits: initialOtherCredits,
				// Metadata
				completedSections: new Set<SectionId>(),

				// Action implementations
				updatePersonalInfo: (data) =>
					set((state) => ({
						personalInfo: { ...state.personalInfo, ...data }
					})),

				updateActivityOrBusiness: (data) =>
					set((state) => ({
						activityOrBusiness: { ...state.activityOrBusiness, ...data }
					})),

				updateCreditDetails: (data) =>
					set((state) => ({
						creditDetails: { ...state.creditDetails, ...data }
					})),

				updateMonthlyIncome: (data) =>
					set((state) => ({
						monthlyIncome: { ...state.monthlyIncome, ...data }
					})),

				updateMonthlyBusinessExpenses: (data) =>
					set((state) => ({
						monthlyBusinessExpenses: { ...state.monthlyBusinessExpenses, ...data }
					})),

				updateMonthlyHouseExpenses: (data) =>
					set((state) => ({
						monthlyHouseExpenses: { ...state.monthlyHouseExpenses, ...data }
					})),

				updateValueOfAssets: (data) =>
					set((state) => ({
						valueOfAssets: { ...state.valueOfAssets, ...data }
					})),

				updateSection: (sectionId, data) =>
					set((state) => ({
						[sectionId]: { ...state[sectionId], ...data }
					})),

				updateOtherCredits: (data) =>
					set((state) => ({
						otherCredits: { ...state.otherCredits, ...data }
					})),

				getAllFormData: () => {
					const {
						personalInfo,
						activityOrBusiness,
						creditDetails,
						monthlyIncome,
						monthlyBusinessExpenses,
						monthlyHouseExpenses,
						valueOfAssets,
						otherCredits
					} = get();
					return {
						personalInfo,
						activityOrBusiness,
						creditDetails,
						monthlyIncome,
						monthlyBusinessExpenses,
						monthlyHouseExpenses,
						valueOfAssets,
						otherCredits
					};
				},

				resetForm: () =>
					set({
						personalInfo: initialPersonalInfo,
						activityOrBusiness: initialActivityOrBusiness,
						creditDetails: initialCreditDetails,
						monthlyIncome: initialMonthlyIncome,
						monthlyBusinessExpenses: initialMonthlyBusinessExpenses,
						monthlyHouseExpenses: initialMonthlyHouseExpenses,
						valueOfAssets: initialValueOfAssets,
						otherCredits: initialOtherCredits,
						completedSections: new Set<SectionId>()
					})
			}),
			{
				name: 'questionnaire-form-storage',
				partialize: (state) => ({
					...state,
					completedSections: Array.from(state.completedSections)
				})
			}
		),
		{ name: 'Form Store' }
	)
);
